/**
 * ReminderProvider — F 490's app-wide reminder context.
 *
 * WHY A PROVIDER
 * --------------
 * The reminder feature touches four places that are not in one another's
 * subtree: the header's bell, the toast, and the date surfaces that carry a
 * clock. The toast in particular has to live ABOVE them all — it is a
 * portal at the document root — and it has to be driven by the SAME poll
 * that drives the bell, or the badge and the toast would disagree about
 * what is unread.
 *
 * So the provider owns:
 *   - the single `ReminderStore` instance (so the store is not rebuilt on
 *     every render, which would drop its in-memory state);
 *   - the single notification poll;
 *   - the toast's open/closed state.
 *
 * AND THE PROVIDER IS THE ONLY PLACE THAT KNOWS THE STORE
 * --------------------------------------------------------
 * The store is created ONCE, lazily, and handed out through context. If
 * each date surface built its own store, the toast's poll and the bell's
 * poll would be two independent polls, and a notification could be toasted
 * in one and counted in the other.
 */
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { Notification } from '../../../types/models/Reminder';
import { ReminderStore } from '../../../core/reminder/store';
import { useNotifications, type UseNotificationsResult } from '../../hooks/useNotifications';

export interface ReminderContextValue extends UseNotificationsResult {
  /** The store. */
  store: ReminderStore;
  /** The toast's notification, or null. */
  toast: Notification | null;
  /** Show the toast for a notification. */
  showToast: (n: Notification) => void;
  /** Hide the toast. */
  dismissToast: () => void;
}

const ReminderContext = createContext<ReminderContextValue | null>(null);

export interface ReminderProviderProps {
  children: React.ReactNode;
  /** Inject a store — used by the tests. */
  store?: ReminderStore;
}

export const ReminderProvider: React.FC<ReminderProviderProps> = ({ children, store }) => {
  // ONE store instance for the whole app.
  const storeRef = useMemo(() => store ?? new ReminderStore(), [store]);
  const feed = useNotifications(storeRef);
  const [toast, setToast] = useState<Notification | null>(null);

  const showToast = useCallback((n: Notification) => setToast(n), []);
  const dismissToast = useCallback(() => setToast(null), []);

  const value = useMemo<ReminderContextValue>(
    () => ({ ...feed, store: storeRef, toast, showToast, dismissToast }),
    [feed, storeRef, toast, showToast, dismissToast],
  );

  return <ReminderContext.Provider value={value}>{children}</ReminderContext.Provider>;
};

/**
 * Read the reminder context.
 *
 * It THROWS when there is no provider, rather than returning a null store.
 * A null store would defer the failure to the first click, in a component
 * that is nowhere near this one; throwing here names the real problem at
 * the point of the mistake.
 */
export function useReminderContext(): ReminderContextValue {
  const ctx = useContext(ReminderContext);
  if (!ctx) {
    throw new Error('useReminderContext must be used inside a <ReminderProvider>');
  }
  return ctx;
}

/**
 * Read the reminder context, or null.
 *
 * For components that are OPTIONAL consumers — a surface that shows a clock
 * only when the provider happens to be there.
 */
export function useOptionalReminderContext(): ReminderContextValue | null {
  return useContext(ReminderContext);
}
